import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute as ContextRoute } from '@angular/router';
import { Optional as ContextOptional, ElementRef as ContextElement } from '@angular/core';
import { Subscription as ContextSubscription } from 'rxjs';
import { viewContextKey, viewContextVisible } from '../../../shared/view-context';
import { PreviewLocation } from '../../../map/components/location-preview/location-preview.component';
import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataPage, DataQuery } from '../../../models/data-source';
import { MapFeatureColors } from '../../../map/core/map-object-colors';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { LeadApiService } from '../../../services/lead-api.service';
import { DataFilterField, DataFilterOption, DataFilterValues } from '../../../shared/ui/data-filter/data-filter.component';
import { DataFilterReader, matchesFilters } from '../../../shared/ui/data-filter/data-filter.match';
import { WorkflowMilestone } from '../../../components/milestone-rail/milestone-rail.component';
import { Observable, Subject, of, throwError, timer } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil, tap, timeout } from 'rxjs/operators';
import { DataTableColumn, DataTableComponent } from '../../../shared/ui/data-table/data-table.component';
import { AuthService } from '../../../services/auth.service';
import { MapFeatureManagerService } from '../../../map/core/map-feature-manager.service';
import { MapNavigationService } from '../../../map/core/map-navigation.service';
import {
  SalesRouteFeature,
} from '../../../map/features/sales-routes/sales-route-feature';
import {
  parseLocalDateString, toLocalDateString, todayLocalDateString,
} from '../../../shared/date.util';
import { EditSession } from '../../../shared/edit-session/edit-session';
import { EditSessionRegistry } from '../../../shared/edit-session/edit-session-registry.service';
import {
  CostContext, DayEndpoints, DayOp, DayState, DayStop, EMPTY_DAY,
  insertionIndexFor, isDayOp, metersPerSecondFor,
  reduceDay, stopKey,
} from './sales-route-session';
import {
  AssignableRep, MANAGE_ANY, MANAGE_OWN, RosterEntry, SalesRoute,
  SalesRouteCandidate, SalesRouteCandidates, SalesRouteDaySaveResult, SalesRouteStop,
  RouteEndpointLocation,
} from './sales-route.models';
import {
  candidateToStop, sortCandidates, toDayState, toRouteStop,
} from './sales-route-mapping';
import { SalesRouteVisitRecorder } from './sales-route-visit';

type Endpoint = 'start' | 'end';

interface NavigationPoint { lat: number; lng: number; }

@Component({
  selector: 'app-sales-route',
  templateUrl: './sales-route.component.html',
  styleUrls: ['./sales-route.component.css'],
})
export class SalesRouteComponent implements OnInit, OnDestroy {
  @ViewChild('routeTable') routeTable?: DataTableComponent<RosterEntry>;
  @ViewChild('routeList') routeList?: DataListComponent<RosterEntry>;
  @ViewChild('candidateTable') candidateTable?: DataTableComponent<SalesRouteCandidate>;
  private readonly destroy$ = new Subject<void>();
  workflowOpen = false;
  workflowEditing = false;
  workflowStep = 2;
  workflowProgress = 2;
  readonly workflowLabels = ['Leads', 'Plan route', 'Visit'];
  incomingIds: number[] = [];
  incomingReads = 0;
  rescheduleStop: SalesRouteStop | null = null;
  rescheduleDate = '';
  workflowMessage: string | null = null;
  private workflowSignature = '';
  private workflowMembers = '';
  @ViewChild('candidateList') candidateList?: DataListComponent<SalesRouteCandidate>;
  rescheduleOpen = false;
  readonly routeColumns: DataTableColumn[] = [
    { id: 'route', label: 'Route' }, { id: 'plannedStops', label: 'Stops' },
    { id: 'progress', label: 'Status' },
  ];
  readonly pickerColumns: DataTableColumn[] = [
    { id: 'title', label: 'Lead', sortKey: 'name' }, { id: 'address', label: 'Address' },
    { id: 'boardColumn', label: 'Stage', sortKey: 'stage' }, { id: 'nextActionDue', label: 'Due', sortKey: 'due' },
  ];
  readonly routeFetch = (q: DataQuery): Observable<DataPage<RosterEntry>> => {
    let rows = this.roster.filter(r => r.plannedStops > 0 && r.ownerUserId === (this.viewOwnerId ?? this.myUserId));
    if (q.sort) rows = [...rows].sort((a,b) => {
      const value = q.sort === 'plannedStops' ? a.plannedStops-b.plannedStops
        : (q.sort === 'progress' ? this.workflowStatus(a).localeCompare(this.workflowStatus(b)) : a.salesRouteId - b.salesRouteId);
      return value * (q.desc ? -1 : 1);
    });
    const from = (q.page - 1) * q.pageSize;
    return of({ items: rows.slice(from, from + q.pageSize), total: rows.length });
  };
  readonly candidateFetch = (q: DataQuery): Observable<DataPage<SalesRouteCandidate>> => {
    const search = (q.search ?? '').toLowerCase();
    const rows = sortCandidates(this.addableCandidates.filter(c => `${c.title} ${c.address}`.toLowerCase().includes(search)
      && matchesFilters(c, this.candidateFilterValues, this.candidateFilterReaders)), q.sort ?? '', q.desc ?? false);
    const from = (q.page - 1) * q.pageSize;
    return of({ items: rows.slice(from, from + q.pageSize), total: rows.length });
  };
  get workflowEndpoints(): DayEndpoints | undefined {
    const e = this.state.endpoints ?? { startAddress: null, endAddress: null, startLat: null, startLng: null,
      endLat: null, endLng: null, returnsToStart: false };
    // Ordering, preview and save must use the endpoints that will be persisted.
    return e.returnsToStart ? { ...e, endAddress: e.startAddress, endLat: e.startLat, endLng: e.startLng } : e;
  }
  get workflowValid(): boolean {
    return this.incomingReads === 0 && (this.state.stops.length > 0 || !!this.route) && this.state.stops.length <= this.maxStops
      && this.state.stops.every(s => s.lat != null && s.lng != null && Number.isFinite(s.lat) && Number.isFinite(s.lng));
  }
  get workflowCanWrite(): boolean { return !this.dayLoadFailed && !this.isPastDay && (this.canManageAny || this.canManageOwn); }
  get nextStop(): SalesRouteStop | undefined { return this.route?.stops.find(s => s.status === 'Pending'); }
  workflowStatus(row: RosterEntry): string {
    const done = row.visited + row.skipped + (row.rescheduled ?? 0);
    if (this.isPastDay) return done === row.plannedStops ? 'Complete' : 'Missed visits';
    return done === 0 ? 'Not started' : done === row.plannedStops ? 'Complete' : 'In progress';
  }
  refreshWorkflowList(): void { this.routeStops.clear(); this.routeTable?.refresh(); this.routeList?.reload(); }
  readonly trackRoute = (_: number, row: RosterEntry) => row.salesRouteId;
  /** Stops of a listed route, read once when its row is expanded. */
  private readonly routeStops = new Map<number, SalesRouteStop[] | null>();
  routeStopsFor(row: RosterEntry): SalesRouteStop[] | null {
    if (!this.routeStops.has(row.salesRouteId)) {
      this.routeStops.set(row.salesRouteId, null);
      const params: Record<string, string> = { date: this.date, id: String(row.salesRouteId) };
      if (row.ownerUserId !== this.myUserId) params['owner'] = String(row.ownerUserId);
      this.http.get<SalesRoute | null>('/api/sales-routes', { params }).pipe(takeUntil(this.destroy$))
        .subscribe({ next: route => this.routeStops.set(row.salesRouteId, route?.stops ?? []), error: () => this.routeStops.set(row.salesRouteId, []) });
    }
    return this.routeStops.get(row.salesRouteId) ?? null;
  }
  openWorkflowRow(row: RosterEntry): void {
    this.workflowOpen = true; this.workflowEditing = false; this.workflowStep = this.workflowProgress = 2;
    this.viewOwnerId = row.ownerUserId === this.myUserId ? null : row.ownerUserId;
    this.routeId = row.salesRouteId; this.clearRecorders();
    this.load();
  }
  /** Add always starts a new route; saved routes are opened from the list. */
  beginWorkflow(): void {
    if (!this.workflowCanWrite || this.loading) return;
    this.workflowOpen = true; this.workflowEditing = true; this.workflowStep = this.workflowProgress = 0;
    this.routeId = null; this.route = null; this.adoptDay();
    if (!this.state.endpoints && this.workflowEndpoints) this.session?.apply({kind: 'endpoints', endpoints: this.workflowEndpoints, label: 'Set route endpoints'});
    this.openAdd();
  }
  leaveWorkflow(): void {
    if (this.planning || this.session?.saving) return;
    this.workflowOpen = false; this.workflowEditing = false; this.routeId = null;
    this.workflowProgress = this.workflowStep = 2;
    this.rescheduleStop = null; this.rescheduleOpen = false; this.incomingIds = [];
    this.workflowMessage = null;
    this.session?.cancel(); this.closeAdd(); this.clearRecorders(); this.load();
  }
  chooseWorkflowStep(index: number): void { if (!this.planning && index <= this.workflowProgress) { this.workflowStep = index; if (index === 0 && this.workflowEditing) this.openAdd(); } }
  /** Footer Save / Save and next: ticked Leads join the route, then the day is saved. */
  saveStep(advance: boolean): void {
    if (!this.canEditDay) return;
    if (!this.canAddSelection) {
      this.loadError = `Choose at most ${this.maxStops} Leads for this route.`;
      return;
    }
    if (this.workflowStep === 0 && this.candidateSelection.length) this.addSelectedCandidates();
    if (advance) this.nextWorkflowStep(); else this.saveWorkflowStep(false);
  }

  /** Saves the day, then moves to the next step; a failed save stays put. */
  nextWorkflowStep(): void { this.saveWorkflowStep(true); }

  private saveWorkflowStep(advance: boolean): void {
    if (this.planning || this.workflowStep >= 2 || !this.session || this.session.saving || !this.canEditDay) return;
    this.loadError = this.planningValidationError();
    if (this.loadError) return;
    const step = this.workflowStep;
    this.session.save().pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        if (!advance) return;
        if (step === 1) { this.onWorkflowSaved(); return; }
        this.workflowProgress = Math.max(this.workflowProgress, step + 1);
        this.workflowStep = step + 1; this.closeAdd();
      },
      error: err => { this.loadError = err?.error?.error ?? err?.message ?? 'Could not save the route. Please try again.'; },
    });
  }
  editWorkflowStep(): void {
    if (!this.workflowCanWrite) return;
    this.workflowEditing = true; this.workflowProgress = Math.min(this.workflowStep, 1); this.workflowStep = this.workflowProgress;
    if (this.workflowStep === 0) this.openAdd();
    this.render();
  }
  private invalidateWorkflow(): void {
    const signature = JSON.stringify([this.state.stops.map(s => s.key), this.state.endpoints]);
    const members = this.state.stops.map(s => s.key).sort().join('|');
    if (this.workflowEditing && signature !== this.workflowSignature) {
      this.orderError = null;
      this.workflowProgress = Math.min(this.workflowProgress, members !== this.workflowMembers ? 0 : 1);
      this.workflowStep = Math.min(this.workflowStep, this.workflowProgress);
    }
    this.workflowMembers = members;
    this.workflowSignature = signature;
    this.refreshPicker();
  }
  readonly endpointKinds: Endpoint[] = ['start', 'end'];
  readonly displayEndpoint = (location: RouteEndpointLocation | string): string => typeof location === 'string' ? location : location?.label ?? '';
  get endpointsReady(): boolean { return this.endpointKinds.every(which => this.endpointReady(which)); }
  endpointReady(which: Endpoint): boolean {
    if (which === 'end' && this.state.endpoints?.returnsToStart) return true;
    const text = this.endpointFields[which].input.trim();
    const e = this.state.endpoints;
    return !text || (text === this.endpointLabel(which) && e?.[`${which}Lat`] != null && e?.[`${which}Lng`] != null);
  }
  private planningValidationError(): string | null {
    if (this.incomingReads > 0) return 'Leads are still loading. Try again when they appear.';
    if (!this.state.stops.length && !this.route) return 'Choose at least one Lead before saving or optimizing.';
    if (this.state.stops.length > this.maxStops) return `Choose at most ${this.maxStops} Leads for this route.`;
    if (!this.workflowValid) return 'Every selected Lead needs a map location. Remove Leads without a location or update them first.';
    for (const which of this.endpointKinds) {
      if (!this.endpointReady(which)) {
        const message = `Choose a matching ${which === 'start' ? 'Start' : 'End'} address from the suggestions, or clear the optional address.`;
        this.endpointFields[which].error = message;
        return message;
      }
    }
    return null;
  }
  endpointLabel(which: Endpoint): string {
    const e = this.state.endpoints;
    const lat = e?.[`${which}Lat`], lng = e?.[`${which}Lng`];
    return e?.[`${which}Address`] || (lat != null && lng != null ? this.coords(lat, lng) : '');
  }
  changeEndpoint(which: Endpoint, value: string): void {
    if (!this.canEditDay || typeof value !== 'string') return;
    const field = this.endpointFields[which];
    field.input = value; field.error = null; field.suggestions = [];
    field.searching = value.trim().length >= 3;
    this.session?.apply({ kind: 'endpoints', label: `Change optional ${which}`, endpoints: {
      ...this.workflowEndpoints!, [`${which}Address`]: null, [`${which}Lat`]: null, [`${which}Lng`]: null,
      returnsToStart: !!this.workflowEndpoints?.returnsToStart,
    }});
    field.queries.next(value.trim());
  }
  /** Save copies Start into End, including an empty optional Start. */
  setEndSameAsStart(same: boolean): void {
    if (!this.canEditDay) return;
    const end = this.endpointFields.end;
    end.queries.next(''); end.input = ''; end.error = null; end.searching = false; end.suggestions = [];
    this.session?.apply({ kind: 'endpoints', label: same ? 'Finish at the start' : 'Choose an end point', endpoints: {
      ...this.workflowEndpoints!, endAddress: null, endLat: null, endLng: null, returnsToStart: same,
    }});
  }
  selectEndpoint(which: Endpoint, location: RouteEndpointLocation): void {
    if (!this.canEditDay) return;
    const field = this.endpointFields[which];
    field.queries.next('');
    field.input = location.label; field.error = null;
    field.searching = false; field.suggestions = [];
    this.session?.apply({ kind: 'endpoints', label: `Set optional ${which}`, endpoints: {
      ...this.workflowEndpoints!, [`${which}Address`]: location.label,
      [`${which}Lat`]: location.latitude, [`${which}Lng`]: location.longitude,
      returnsToStart: which === 'start' && !!this.workflowEndpoints?.returnsToStart,
    }});
  }
  private watchEndpointSearch(which: Endpoint): void {
    const field = this.endpointFields[which];
    // The existing address-search endpoint resolves either end of a route.
    field.queries.pipe(switchMap(query => query.length < 3 ? of([] as RouteEndpointLocation[]) :
      timer(300).pipe(switchMap(() => this.http.get<RouteEndpointLocation[]>('/api/sales-routes/start-locations', { params: { query } })),
        timeout(12000), catchError(err => {
          field.error = err?.error?.error ?? `Could not find the address. Try again or leave the ${which} empty.`;
          return of([] as RouteEndpointLocation[]);
        }))), takeUntil(this.destroy$)).subscribe(locations => {
      field.searching = false;
      field.suggestions = locations;
      if (field.input.trim().length >= 3 && !this.endpointReady(which) && !locations.length && !field.error)
        field.error = `No matching address. Try a fuller address or leave the ${which} empty.`;
    });
  }
  get workflowRepLabel(): string { return this.userLabel(this.viewOwnerId ?? this.myUserId); }
  workflowEndpointLabel(which: 'start' | 'finish'): string {
    const e = this.workflowEndpoints;
    if (!e) return 'Not set';
    if (which === 'finish' && e.returnsToStart) return 'Same as start';
    const lat = which === 'start' ? e.startLat : e.endLat;
    const lng = which === 'start' ? e.startLng : e.endLng;
    if (lat == null || lng == null) return which === 'start' ? 'First Lead' : 'Last Lead';
    return (which === 'start' ? e.startAddress : e.endAddress) || this.coords(lat,lng);
  }
  private refreshPicker(): void { this.candidateTable?.refresh(); this.candidateList?.reload(); }
  /** The planning steps are saved; the route opens on its Visit step. */
  private onWorkflowSaved(): void {
    this.workflowEditing = false; this.workflowStep = this.workflowProgress = 2; this.rescheduleStop = null;
    this.closeAdd(); this.load();
  }
  private afterWorkflowLoad(): void {
    if (this.workflowEditing && this.isPastDay) {
      this.workflowEditing = false; this.workflowStep = this.workflowProgress = 2;
    }
    if (this.workflowEditing) {
      this.workflowStep = this.workflowProgress = 0;
      const request = this.dayLoadSeq;
      const ids = this.incomingIds.splice(0);
      this.incomingReads = ids.length;
      for (const id of ids) this.leadApi.getProfile(id).pipe(takeUntil(this.destroy$)).subscribe({ next: lead => {
        if (request !== this.dayLoadSeq) return;
        this.incomingReads--;
        if (lead.latitude == null || lead.longitude == null) { this.workflowMessage = `${lead.name}: update its map location before adding it.`; return; }
        this.addCandidate({ leadId: lead.id, title: lead.name, address: [lead.addressLine1, lead.city, lead.state].filter(Boolean).join(', '),
          lat: lead.latitude, lng: lead.longitude, value: 0, nextActionDue: null, daysSinceLastActivity: null,
          boardColumn: lead.leadStageName, reasons: [], ownerUserId: lead.salesRepUserId, ownerName: null });
      }, error: () => { if (request !== this.dayLoadSeq) return; this.incomingReads--; this.workflowMessage = 'A selected Lead could not be loaded.'; } });
      if (this.rescheduleStop) {
        const stop = this.rescheduleStop;
        const held = this.state.stops.find(s => s.objectId === stop.objectId);
        if (held && held.status !== 'Pending') this.workflowMessage = 'This Lead was already processed on the target date. Choose another date.';
        else if (!held) this.session?.apply({ kind: 'add', at: null, label: 'Reschedule this visit', stop: { ...toDayState({ ...this.route!, stops: [stop] }).stops[0], stopId: null, status: 'Pending', savedIndex: -1 } });
      }
      this.openAdd();
    }
    for (const which of this.endpointKinds) {
      const field = this.endpointFields[which];
      field.queries.next('');
      field.input = this.endpointLabel(which); field.error = null; field.searching = false; field.suggestions = [];
    }
    this.render(); this.refreshWorkflowList();
  }
  beginReschedule(stop: SalesRouteStop): void {
    this.rescheduleStop = stop; this.rescheduleOpen = true;
    const next = parseLocalDateString(this.date)!; next.setDate(next.getDate() + 1);
    this.rescheduleDate = this.isPastDay ? this.todayLocal() : toLocalDateString(next);
  }
  confirmReschedule(): void {
    if (!this.rescheduleStop || this.rescheduleDate <= this.date || this.rescheduleDate < this.todayLocal()) return;
    this.rescheduleOpen = false;
    if (this.isPastDay || this.rescheduleStop.status !== 'Pending') { this.incomingIds = [this.rescheduleStop.objectId]; this.rescheduleStop = null; }
    this.workflowEditing = true; this.workflowOpen = true; this.workflowStep = this.workflowProgress = 0;
    this.routeId = null; this.clearRecorders();
    this.date = this.rescheduleDate; this.load();
  }

  static readonly MaxWaypointsPerLink = 3;
  /** Google Routes API orders at most 25 stops in one request. */
  static readonly MaxStops = 25;
  readonly maxStops = SalesRouteComponent.MaxStops;

  route: SalesRoute | null = null;
  /** The route being viewed or edited; null for a new route from Add. */
  routeId: number | null = null;
  private dayLoadSeq = 0;

  private readEntry(): void {
    const params = this.activatedRoute.snapshot.queryParamMap;
    if (!params.has('lead') && !params.has('leads') && !params.has('date') && !params.has('routeId')) return;
    this.workflowOpen = true;
    this.workflowEditing = params.has('lead') || params.has('leads');
    this.workflowProgress = this.workflowEditing ? 0 : 2;
    this.workflowStep = this.workflowProgress;
    const date = params.get('date');
    const parsed = date ? parseLocalDateString(date) : null;
    if (date && (!parsed || toLocalDateString(parsed) !== date)) {
      this.dayError = 'Invalid date in the link. Showing today; choose the visit date above.';
    } else if (date) {
      this.date = date;
    }
    const owner = Number(params.get('owner'));
    if (Number.isSafeInteger(owner) && owner > 0 && this.canManageAny)
      this.viewOwnerId = owner === this.myUserId ? null : owner;
    const routeId = Number(params.get('routeId'));
    if (!this.workflowEditing && Number.isSafeInteger(routeId) && routeId > 0) this.routeId = routeId;
    const ids = [params.get('lead') ?? '', ...(params.get('leads') ?? '').split(',')];
    this.incomingIds = ids.map(Number).filter(id => Number.isSafeInteger(id) && id > 0).slice(0, this.maxStops);
  }
  roster: RosterEntry[] = [];
  date = this.todayLocal();
  loading = false;
  loadError: string | null = null;
  dayLoadFailed = false;
  routePreviewPoints: PreviewLocation[] = [];
  readonly routeColor = MapFeatureColors.salesRoutes;
  planning = false;

  orderError: string | null = null;
  private readonly orderCanceled$ = new Subject<void>();

  viewOwnerId: number | null = null;

  reps: AssignableRep[] = [];
  repLoadError: string | null = null;

  dayError: string | null = null;

  readonly endpointFields: Record<Endpoint, {
    input: string; searching: boolean; error: string | null;
    suggestions: RouteEndpointLocation[]; queries: Subject<string>;
  }> = {
    start: { input: '', searching: false, error: null, suggestions: [], queries: new Subject<string>() },
    end: { input: '', searching: false, error: null, suggestions: [], queries: new Subject<string>() },
  };

  /** One result form per pending stop, so the Visit list can be filled top to bottom. */
  private readonly recorders = new Map<number, SalesRouteVisitRecorder>();
  recorderFor(stop: SalesRouteStop): SalesRouteVisitRecorder {
    let recorder = this.recorders.get(stop.id);
    if (!recorder) {
      recorder = new SalesRouteVisitRecorder(this.http, () => this.load());
      recorder.openFor(stop);
      this.recorders.set(stop.id, recorder);
    }
    return recorder;
  }
  private clearRecorders(): void {
    this.recorders.forEach(recorder => recorder.destroy());
    this.recorders.clear();
  }

  session: EditSession<DayState, DayOp> | null = null;

  private releaseCopilot: () => void = () => {};

  addPool: SalesRouteCandidates | null = null;
  addPoolError: string | null = null;

  private readonly myUserId: number | null;

  readonly canManageAny: boolean;
  readonly canManageOwn: boolean;

  private viewRegistration?: ContextSubscription;
  private registerViewContext(): void {
    this.viewRegistration = this.viewContext?.registerView({
      key: viewContextKey(this.viewRoute, 'Sales Route view'), name: 'Sales Route view', priority: -10,
      active: () => viewContextVisible(this.viewElement),
      read: () => ({ date: this.date, ownerId: this.viewOwnerId }),
      describe: state => JSON.stringify(state),
      restore: state => { if (this.session?.dirty) return; this.date = state.date; this.viewOwnerId = state.ownerId; this.load(); },
    });
  }

  constructor(
    private http: HttpClient,
    auth: AuthService,
    private feature: SalesRouteFeature,
    private featureManager: MapFeatureManagerService,
    private registry: EditSessionRegistry,
    private activatedRoute: ActivatedRoute,
    private leadApi: LeadApiService,
    private mapNavigation: MapNavigationService,
    @ContextOptional() private readonly viewContext?: AppContextService,
    @ContextOptional() private readonly viewRoute?: ContextRoute,
    @ContextOptional() private readonly viewElement?: ContextElement<HTMLElement>,
  ) {
    this.myUserId = auth.tenant.currentUserId;
    this.canManageAny = auth.hasPermission?.(MANAGE_ANY) ?? false;
    this.canManageOwn = auth.hasPermission?.(MANAGE_OWN) ?? false;
  }

  ngOnInit(): void {
    this.registerViewContext();
    this.readEntry();
    this.feature.resetFraming();
    this.feature.dropped$.pipe(takeUntil(this.destroy$))
      .subscribe(at => this.onMapDrop(at.key, at.lat, at.lng));

    this.endpointKinds.forEach(which => this.watchEndpointSearch(which));
    this.http.get<AssignableRep[]>('/api/sales-routes/reps').pipe(takeUntil(this.destroy$)).subscribe({
      next: rows => { this.reps = rows; this.buildCandidateFilterFields(); this.refreshWorkflowList(); },
      error: () => { this.repLoadError = 'Could not load sales reps. Refresh to try again.'; },
    });

    this.load();
  }

  get workflowMilestones(): WorkflowMilestone[] {
    return this.workflowLabels.map((label, index) => ({
      id: String(index), label,
      complete: index < this.workflowProgress || (index === 2 && !this.workflowEditing && this.stops.length > 0 && this.doneCount === this.stops.length),
      disabled: index > this.workflowProgress || this.loading || this.planning || !!this.session?.saving,
    }));
  }

  /** Camera only: selecting the Lead would replace this Workspace with its details. */
  showStopOnMap(stop: SalesRouteStop): void {
    if (stop.lat == null || stop.lng == null) return;
    this.mapNavigation.navigateTo({ latitude: stop.lat, longitude: stop.lng, zoom: 17 });
  }

  focusStop(stop: DayStop | SalesRouteStop): void {
    if (stop.lat == null || stop.lng == null) return;
    if (!this.feature.enabled) this.featureManager.toggleFeature(SalesRouteFeature.Key);
    this.feature.focusStop(stopKey(stop.objectType, stop.objectId));
  }

  ngOnDestroy(): void {
    this.viewRegistration?.unsubscribe();
    // The plan's pins go; the workspace map they were drawn on stays.
    this.destroy$.next();
    this.destroy$.complete();
    this.releaseCopilot();
    this.session?.destroy();
    this.feature.setDraggable(false);
    this.clearRecorders();
    this.orderCanceled$.complete();
  }

  private todayLocal(): string {
    return todayLocalDateString();
  }

  private dayParams(): Record<string, string> {
    const params: Record<string, string> = { date: this.date };
    if (this.viewOwnerId != null) params['owner'] = String(this.viewOwnerId);
    if (this.routeId != null) params['id'] = String(this.routeId);
    return params;
  }

  get showRepPicker(): boolean { return this.canManageAny; }

  get repChoice(): number | null { return this.viewOwnerId ?? this.myUserId; }

  onOwnerChange(value: number): void {
    if (this.planning || this.session?.saving || !this.canManageAny
      || !Number.isSafeInteger(value) || !this.reps.some(rep => rep.userId === value) || value === this.repChoice) return;
    const owner = value === this.myUserId ? null : value;
    this.viewOwnerId = owner; this.routeId = null;
    if (!this.workflowEditing) this.rescheduleStop = null;
    if (!this.workflowOpen) this.workflowEditing = false;
    this.closeAdd();
    this.load();
  }

  repLabel(rep: AssignableRep): string {
    return rep.displayName?.trim() || rep.userName?.trim() || `Rep ${rep.userId}`;
  }

  userLabel(userId: number | null): string {
    if (userId == null) return 'nobody';
    const rep = this.reps.find(r => r.userId === userId);
    return rep ? this.repLabel(rep) : `Rep ${userId}`;
  }

  load(): void {
    this.stopPlanning();
    const request = ++this.dayLoadSeq;
    this.incomingReads = 0;
    if (this.workflowEditing) this.workflowStep = this.workflowProgress = 0;
    this.readRoster();

    this.loading = true;
    this.dayLoadFailed = false;
    this.loadError = null;
    // A new route from Add has nothing saved yet; never adopt another route of the same day.
    (this.workflowEditing && this.routeId == null
      ? of(null)
      : this.http.get<SalesRoute | null>('/api/sales-routes', { params: this.dayParams() }))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: route => {
          if (request !== this.dayLoadSeq) return;
          this.route = route ?? null;
          this.adoptDay();
          this.loading = false;
          this.afterWorkflowLoad();
        },
        error: err => {
          if (request !== this.dayLoadSeq) return;
          this.loading = false;
          this.dayLoadFailed = true;
          this.loadError = err?.status === 403
            ? (err.error?.error ?? 'That is not your day.')
            : 'Could not load the day.';
        },
      });
  }

  private readRoster(): void {
    this.boardError = null;
    const requestedDate = this.date;
    this.http.get<{ reps: RosterEntry[] }>(
      '/api/sales-routes/roster', { params: { date: this.date } })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: res => { if (this.date !== requestedDate) return; this.roster = res?.reps ?? []; this.refreshWorkflowList(); },
        error: err => {
          this.boardError = err?.status === 403
            ? 'You cannot read this team roster.'
            : 'The team roster could not be read, so no plan is shown against these reps.';
          this.roster = [];
          this.refreshWorkflowList();
        },
      });
  }

  boardError: string | null = null;

  onDateChange(value: string): void {
    if (!value || !parseLocalDateString(value) || this.planning || this.session?.saving || value === this.date) return;
    // The date is an attribute of a new route, not navigation to another day.
    // Keep the mounted picker (including its selection) and the whole draft.
    if (this.workflowEditing && this.route == null) {
      this.date = value;
      this.dayError = null;
      return;
    }
    this.date = value; this.routeId = null;
    if (!this.workflowEditing) this.rescheduleStop = null;
    if (!this.workflowOpen) this.workflowEditing = false;
    this.closeAdd(); this.load();
  }

  get canPlan(): boolean { return !this.isPastDay; }

  get isPastDay(): boolean { return this.date < this.todayLocal(); }

  private planRequest(leadIds: number[]): Record<string, unknown> {
    return {
      leadIds,
      startLat: this.workflowEndpoints?.startLat ?? null,
      startLng: this.workflowEndpoints?.startLng ?? null,
      endLat: this.workflowEndpoints?.endLat ?? null,
      endLng: this.workflowEndpoints?.endLng ?? null,
      finishAtStart: this.workflowEndpoints?.returnsToStart ?? false,
      ownerUserId: this.viewOwnerId,
    };
  }

  private stopsToOrder(): number[] { return this.state.stops.filter(s => s.status === 'Pending' && s.objectType === 'Lead').map(s => s.objectId); }

  planWithAgent(): void {
    if (this.planning || !this.canPlan || !this.canEditDay) return;
    this.orderError = this.planningValidationError();
    if (this.orderError) return;
    if (this.state.stops.some(stop => stop.objectType !== 'Lead')) {
      this.orderError = 'Automatic ordering currently supports Lead stops only.';
      return;
    }
    this.orderStops(this.stopsToOrder());
  }

  private orderStops(ids: number[]): void {
    this.orderError = null;
    if (ids.length === 0 || ids.length > SalesRouteComponent.MaxStops) {
      this.orderError = `Select 1–${SalesRouteComponent.MaxStops} Leads before optimizing.`;
      return;
    }
    this.planning = true;
    this.render();
    this.http.post<{ orderedLeadIds: number[] }>('/api/sales-routes/order', this.planRequest(ids))
      .pipe(timeout(20000), takeUntil(this.orderCanceled$), takeUntil(this.destroy$),
        finalize(() => { this.planning = false; this.render(); }))
      .subscribe({
        next: result => this.applyOrder(result?.orderedLeadIds),
        error: err => {
          this.orderError = err?.name === 'TimeoutError'
            ? 'Route ordering timed out. Try again or adjust the order manually.'
            : err?.status === 403 ? 'You do not have permission to optimize this route.'
              : (err?.error?.error ?? 'Could not optimize this route. Try again or adjust the order manually.');
        },
      });
  }

  stopPlanning(): void {
    this.orderCanceled$.next();
    this.orderError = null;
  }

  private applyOrder(ids: unknown): void {
    const pending = this.state.stops.filter(stop => stop.status === 'Pending');
    if (!Array.isArray(ids) || ids.length !== pending.length || new Set(ids).size !== ids.length
        || ids.some(id => !Number.isSafeInteger(id) || !pending.some(stop => stop.objectId === id))) {
      this.orderError = 'Google did not return the selected Leads. Your order is unchanged; try again.';
      return;
    }
    const queue = ids.map(id => stopKey('Lead', id));
    const order = this.state.stops.map(stop => stop.status === 'Pending' ? queue.shift()! : stop.key);
    this.session?.apply({ kind: 'reorder', order, label: 'Google stop order · review before saving' });
    this.orderError = null;
  }

  private adoptDay(): void {
    const state = toDayState(this.route);
    if (!this.session) {
      this.session = new EditSession<DayState, DayOp>({
        initial: state,
        reduce: reduceDay,
        commit: (next: DayState) => this.commitDay(next),
      });
      this.session.state$.pipe(takeUntil(this.destroy$)).subscribe(() => { this.invalidateWorkflow(); this.render(); });
      this.offerToCopilot(this.session);
      return;
    }
    this.session.reload(state);
    this.render();
  }

  private offerToCopilot(session: EditSession<DayState, DayOp>): void {
    this.releaseCopilot();
    this.releaseCopilot = this.registry.setCopilotTarget({
      session,
      propose: op => {
        if (!this.canEditDay || !isDayOp(op) || ['transfer', 'withdraw-transfer'].includes(op.kind)) return false;
        session.propose(op);
        return true;
      },
    });
  }

  get state(): DayState { return this.session?.state ?? EMPTY_DAY; }

  get dirty(): boolean { return this.session?.dirty ?? false; }

  get canEditDay(): boolean {
    if (this.loading || this.dayLoadFailed || !this.workflowOpen || this.isPastDay || !this.workflowEditing || this.planning || this.session?.saving) return false;
    return this.canManageAny || (this.canManageOwn && this.viewOwnerId == null);
  }

  private commitDay(state: DayState): Observable<DayState> {
    if (this.loading || this.dayLoadFailed || !this.workflowEditing || this.isPastDay || !this.workflowValid || !this.endpointsReady)
      return throwError(() => new Error('Choose at least one Lead with a map location before saving.'));
    if (!state.stops.length && !confirm('Cancel all remaining visits on this route?'))
      return throwError(() => new Error('Route kept unchanged.'));
    const body = {
      date: this.date,
      ownerUserId: this.viewOwnerId,
      stops: state.stops.map((stop, index) => ({
        objectType: stop.objectType, objectId: stop.objectId, seq: index + 1,
      })),
      moves: [],
      pathGeoJson: null,
      endpoints: this.workflowEndpoints,
      routeId: this.route?.id ?? null,
      createIfMissing: this.route == null,
      rescheduleStopId: this.rescheduleStop && state.stops.some(s => s.objectId === this.rescheduleStop!.objectId) ? this.rescheduleStop.id : null,
    };
    return this.http.put<SalesRouteDaySaveResult>('/api/sales-routes/day', body).pipe(
      tap(result => {
        this.routeId = result.salesRouteId ?? this.routeId;
        // The source was rescheduled atomically by this save; later steps only edit the target.
        this.rescheduleStop = null;
      }),
      switchMap(() => this.fetchDay()),
    );
  }

  private fetchDay(): Observable<DayState> {
    return this.http.get<SalesRoute | null>('/api/sales-routes', { params: this.dayParams() })
      .pipe(map(route => {
        this.route = route ?? null;
        return toDayState(this.route);
      }));
  }

  nudge(key: string, by: number): void {
    const from = this.state.stops.findIndex(s => s.key === key);
    if (from < 0) return;
    this.reorderTo(from, from + by);
  }

  private reorderTo(from: number, to: number): void {
    const stops = this.state.stops;
    const target = Math.min(Math.max(to, 0), stops.length - 1);
    if (!this.canEditDay || !this.session || from === target || from < 0 || from >= stops.length) return;

    const order = stops.map(s => s.key);
    const [moved] = order.splice(from, 1);
    order.splice(target, 0, moved);
    const stop = stops[from];
    this.session.apply({
      kind: 'reorder',
      order,
      label: `${this.stopName(stop)} moved ${from + 1} → ${target + 1}`,
    });
  }

  onMapDrop(key: string, lat: number, lng: number): void {
    if (!this.canEditDay || !this.session) { this.render(); return; }
    const stops = this.state.stops;
    const from = stops.findIndex(s => s.key === key);
    const to = insertionIndexFor(stops, key, { lat, lng }, this.costContext());
    if (from < 0 || from === to) { this.render(); return; }
    this.reorderTo(from, to);
  }

  private costContext(): CostContext {
    return {
      start: this.route?.startLat != null && this.route.startLng != null
        ? { lat: this.route.startLat, lng: this.route.startLng } : null,
      finish: this.route?.endLat != null && this.route.endLng != null
        ? { lat: this.route.endLat, lng: this.route.endLng } : null,
      metersPerSecond: metersPerSecondFor(
        this.route?.plannedDriveMeters, this.route?.plannedDriveSeconds),
    };
  }

  removeStop(stop: DayStop): void {
    if (!this.session || !this.canEditDay || stop.status !== 'Pending') return;
    this.session.apply({
      kind: 'remove',
      key: stop.key,
      label: stop.savedIndex < 0
        ? `${this.stopName(stop)} taken back off today`
        : `${this.stopName(stop)} off this day only · still ${this.dayOwnerLabel}’s`,
    });
  }

  get dayOwnerLabel(): string {
    if (this.route == null) return 'the rep';
    return this.route.ownerUserId === this.myUserId
      ? 'yours' : `${this.userLabel(this.route.ownerUserId)}’s`;
  }

  openAdd(): void { this.loadAddPool(); }

  closeAdd(): void {
    this.addPool = null;
    this.addPoolError = null;
  }

  private loadAddPool(): void {
    this.addPool = null;
    this.addPoolError = null;
    const params: Record<string, string> = { date: this.date };
    if (this.viewOwnerId != null) params['owner'] = String(this.viewOwnerId);
    if (this.routeId != null) params['routeId'] = String(this.routeId);
    const request = this.dayLoadSeq;
    this.http.get<SalesRouteCandidates>('/api/sales-routes/candidates', { params }).pipe(takeUntil(this.destroy$)).subscribe({
      next: pool => { if (request !== this.dayLoadSeq) return; this.addPool = pool; this.buildCandidateFilterFields(); this.refreshPicker(); },
      error: err => {
        this.addPoolError = err?.status === 403
          ? (err.error?.error ?? 'That is not your patch.')
          : 'Could not read that patch.';
      },
    });
  }

  get addableCandidates(): SalesRouteCandidate[] {
    const held = new Set([
      ...this.state.stops.map(s => s.key),
      ...this.state.transfers.map(t => t.stop.key),
    ]);
    return (this.addPool?.items ?? [])
      .filter(c => !held.has(stopKey('Lead', c.leadId)));
  }

  candidateSelection: SalesRouteCandidate[] = [];
  candidateFilterFields: DataFilterField[] = [];
  candidateFilterValues: DataFilterValues = {};
  /** How each candidate filter column is read; every field needs a reader. */
  readonly candidateFilterReaders: Record<string, DataFilterReader<SalesRouteCandidate>> = {
    assignment: row => row.ownerUserId == null ? 'unassigned' : 'assigned',
    stage: row => row.boardColumn,
    title: row => row.title,
    address: row => row.address,
    due: row => row.nextActionDue,
  };
  onCandidateFilterChange(values: DataFilterValues): void {
    this.candidateFilterValues = values;
    this.refreshPicker();
  }
  private buildCandidateFilterFields(): void {
    const none: readonly DataFilterOption[] = [];
    const owner = this.viewOwnerId ?? this.myUserId;
    const rep = this.reps.find(r => r.userId === owner);
    const stages = [...new Set((this.addPool?.items ?? []).map(c => c.boardColumn).filter(Boolean))].sort();
    this.candidateFilterFields = [
      { id: 'assignment', label: 'Assignment', allLabel: 'Assigned and unassigned', options: [
        { value: 'assigned', label: owner === this.myUserId ? 'Assigned to me' : `Assigned to ${rep ? this.repLabel(rep) : 'this sales rep'}` },
        { value: 'unassigned', label: 'Unassigned' },
      ] },
      { id: 'stage', label: 'Stage', pinned: false, allLabel: 'Any stage', options: stages.map(s => ({ value: s, label: s })) },
      { id: 'title', label: 'Lead', kind: 'text', pinned: false, options: none },
      { id: 'address', label: 'Address', kind: 'text', pinned: false, options: none },
      { id: 'due', label: 'Due', kind: 'date', pinned: false, options: none },
    ];
  }
  get canAddSelection(): boolean { return this.state.stops.length + this.candidateSelection.length <= this.maxStops; }
  addSelectedCandidates(): void {
    if (!this.canAddSelection) return;
    const rows = this.candidateSelection;
    this.candidateSelection = [];
    for (const row of rows) this.addCandidate(row);
    this.refreshPicker();
  }

  addCandidate(candidate: SalesRouteCandidate): void {
    if (!this.session || !this.canEditDay || this.state.stops.length >= this.maxStops) return;
    this.session.apply({ kind: 'add', stop: candidateToStop(candidate), at: null,
      label: `${candidate.title ?? 'Lead'} added to this visit route` });
  }

  // The session projection creates new objects; retain each visit form across checks.
  readonly trackVisitStop = (_: number, stop: SalesRouteStop): string => stopKey(stop.objectType, stop.objectId);

  get stops(): SalesRouteStop[] {
    if (!this.session) return this.route?.stops ?? [];
    return this.state.stops.map((stop, index) => toRouteStop(stop, index));
  }

  get doneCount(): number {
    return this.stops.filter(s => s.status !== 'Pending').length;
  }

  stopName(stop: SalesRouteStop | DayStop): string {
    return stop.title?.trim() || `Lead ${stop.objectId}`;
  }

  statusColor(status: string): string {
    switch (status) {
      case 'Visited': return '#2e7d32';
      case 'Skipped': return '#b45309';
      default: return MapFeatureColors.salesRoutes;
    }
  }

  get navSegments(): NavigationPoint[][] {
    if (this.stops.some(s => s.lat == null || s.lng == null)) return [];
    const located: NavigationPoint[] = this.stops.filter(s => s.status === 'Pending').map(s => ({ lat: s.lat!, lng: s.lng! }));
    if (located.length && this.route?.endLat != null && this.route.endLng != null) {
      const last = located[located.length - 1];
      if (last.lat !== this.route.endLat || last.lng !== this.route.endLng)
        located.push({ lat: this.route.endLat, lng: this.route.endLng });
    }
    if (located.length === 0) return [];

    const max = SalesRouteComponent.MaxWaypointsPerLink;
    const externalOrigin = this.doneCount === 0 && this.route?.startLat != null && this.route?.startLng != null;
    const segments: NavigationPoint[][] = [];
    let i = 0;

    while (i < located.length) {
      const capacity = segments.length === 0 && externalOrigin ? max + 1 : max + 2;
      const leg = located.slice(i, i + capacity);
      segments.push(leg);
      if (i + capacity >= located.length) break;
      i += capacity - 1;
    }
    return segments;
  }

  navUrl(segment: NavigationPoint[], isFirstSegment: boolean): string {
    if (segment.length === 0) return '';
    const externalOrigin = isFirstSegment
      && this.doneCount === 0 && this.route?.startLat != null && this.route?.startLng != null;

    const origin = externalOrigin
      ? this.coords(this.route!.startLat!, this.route!.startLng!)
      : this.at(segment[0]);
    const body = externalOrigin ? segment : segment.slice(1);

    if (body.length === 0) {
      return `https://www.google.com/maps/dir/?api=1&origin=${origin}` +
             `&destination=${origin}&travelmode=driving`;
    }
    const destination = this.at(body[body.length - 1]);
    const waypoints = body.slice(0, -1).map(s => this.at(s)).join('|');
    const params = [
      'api=1',
      `origin=${origin}`,
      `destination=${destination}`,
      waypoints ? `waypoints=${encodeURIComponent(waypoints)}` : '',
      'travelmode=driving',
    ].filter(Boolean).join('&');
    return `https://www.google.com/maps/dir/?${params}`;
  }

  stopUrl(stop: SalesRouteStop | DayStop): string {
    return `https://www.google.com/maps/dir/?api=1&destination=${this.at(stop)}` +
           `&travelmode=driving&dir_action=navigate`;
  }

  private at(stop: Pick<SalesRouteStop, 'lat' | 'lng'>): string {
    return this.coords(stop.lat!, stop.lng!);
  }

  private coords(lat: number, lng: number): string {
    const trim = (n: number) => String(Number(n.toFixed(6)));
    return `${trim(lat)},${trim(lng)}`;
  }

  private render(): void {
    const e = this.workflowEndpoints;
    const start = e?.startLat != null && e.startLng != null ? { lat: e.startLat, lng: e.startLng } : null;
    const finish = e?.returnsToStart ? start : e?.endLat != null && e.endLng != null ? { lat: e.endLat, lng: e.endLng } : null;
    const state = this.state;
    this.routePreviewPoints = [
      ...(start ? [{ latitude: start.lat, longitude: start.lng, title: `Start: ${this.workflowEndpointLabel('start')}` }] : []),
      ...state.stops.map((s, i) => ({ latitude: s.lat, longitude: s.lng, sequence: i + 1,
        title: `${i + 1}. ${this.stopName(s)}`, color: this.statusColor(s.status) })),
      ...(finish ? [{ latitude: finish.lat, longitude: finish.lng, title: `Finish: ${this.workflowEndpointLabel('finish')}` }] : []),
    ];
    this.feature.setDraggable(this.canEditDay);
    this.feature.setPlan(start, state.stops.map((s, index) => ({
      seq: index + 1,
      lat: s.lat,
      lng: s.lng,
      title: this.stopName(s),
      color: this.statusColor(s.status),
      key: s.key,
      proposed: state.touched[s.key] != null,
      copilot: state.touched[s.key] === 'copilot',
    })), finish, this.route?.id ?? 'draft');
    this.feature.setPersistedPath(null);
    this.feature.setShape(null);
  }

}
